/*
 * ATBT catalog: client-side search, filter, sort and pagination for sections/atbt-catalog.liquid.
 *
 * Data: one <script type="application/json" data-catalog-json> per section (parsed once).
 * Markup: card() below must stay identical to snippets/atbt-product-card.liquid.
 * Quick view: buttons carry [data-quick="handle"]; window.ATBT (atbt-global.js) handles the click.
 * Works with several instances on a page and re-initialises on shopify:section:load.
 */
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-atbt-catalog]';
  var SRCSET_WIDTHS = [200, 300, 400, 600, 800];
  var SEARCH_DELAY = 150;
  var MODEL_RE = /iPhone\s*(?:case\s*)?(1[1-7]|X[RS]?|Air)\s*(Pro\s*Max|Pro|Plus|Mini|Air|Max)?/i;
  var MODEL_SUFFIX = { promax: 'Pro Max', pro: 'Pro', plus: 'Plus', mini: 'Mini', air: 'Air', max: 'Max' };
  var ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  var DEFAULT_STRINGS = {
    quick_view: 'Quick view',
    sold_out: 'Sold out',
    from: 'From [price]',
    see_options: 'See options',
    options: 'options',
    view_details: 'View details',
    results: '[start]–[end] of [total] products',
    results_none: '0 products',
    empty_heading: 'Nothing here just yet.',
    empty_text: 'Try another search or clear the filters to see more beautiful things.',
    invalid_text: 'The minimum price must be less than or equal to the maximum price.',
    clear: 'Clear filters',
    search_chip: 'Search:',
    stock_chip: 'In stock',
    up_to: 'Up to',
    remove_filter: 'Remove filter',
    explore: 'Explore',
    browse_label: 'View all collections',
    browse_url: '/collections',
    all_models: 'All models',
    prev: 'Previous page',
    next: 'Next page',
    page: 'Page [n]'
  };

  var collator = typeof Intl !== 'undefined' && Intl.Collator
    ? new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' })
    : null;
  var reducedMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  function esc(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
      return ESCAPES[c];
    });
  }

  function compareText(a, b) {
    return collator ? collator.compare(a, b) : String(a).localeCompare(String(b));
  }

  /* Normalises an option value such as "IPhone 15 Pro Max" to "iPhone 15 Pro Max" (design line 169). */
  function phoneModel(name) {
    var m = String(name).match(MODEL_RE);
    if (!m) return null;
    var base = m[1].toLowerCase() === 'air' ? 'Air' : m[1].toUpperCase();
    var suffix = m[2] ? m[2].replace(/\s/g, '').toLowerCase() : '';
    return 'iPhone ' + base + (suffix && MODEL_SUFFIX[suffix] ? ' ' + MODEL_SUFFIX[suffix] : '');
  }

  function formatMoney(cents, format) {
    var atbt = window.ATBT;
    if (atbt && typeof atbt.formatMoney === 'function') {
      try {
        var out = atbt.formatMoney(cents);
        if (typeof out === 'string' && out) return out;
      } catch (e) { /* fall through */ }
    }
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try {
        return window.Shopify.formatMoney(cents, format);
      } catch (e) { /* fall through */ }
    }
    return '$' + (Number(cents) / 100).toFixed(2);
  }

  function debounce(fn, wait) {
    var timer = null;
    return function () {
      clearTimeout(timer);
      timer = setTimeout(fn, wait);
    };
  }

  function fill(template, values) {
    return Object.keys(values).reduce(function (text, key) {
      return text.split('[' + key + ']').join(String(values[key]));
    }, String(template));
  }

  function srcset(url) {
    return SRCSET_WIDTHS.map(function (w) {
      return url.replace(/([?&])width=\d+/, '$1width=' + w) + ' ' + w + 'w';
    }).join(', ');
  }

  function init(root) {
    if (!root || root.hasAttribute('data-catalog-ready')) return;
    var jsonEl = root.querySelector('script[data-catalog-json]');
    if (!jsonEl) return;

    var data;
    try {
      data = JSON.parse(jsonEl.textContent);
    } catch (err) {
      if (window.console) console.warn('[atbt-catalog] Could not parse catalog data', err);
      return;
    }
    root.setAttribute('data-catalog-ready', '');

    var products = Array.isArray(data.products) ? data.products : [];
    var S = {};
    Object.keys(DEFAULT_STRINGS).forEach(function (key) {
      var value = data.strings ? data.strings[key] : null;
      S[key] = typeof value === 'string' && value !== '' ? value : DEFAULT_STRINGS[key];
    });

    function q(selector) {
      return root.querySelector(selector);
    }

    var els = {
      toolbar: q('.catalog-toolbar'),
      search: q('[data-catalog-search]'),
      sort: q('[data-catalog-sort]'),
      toggle: q('[data-filter-toggle]'),
      filters: q('[data-catalog-filters]'),
      collection: q('[data-collection-filter]'),
      min: q('[data-min-price]'),
      max: q('[data-max-price]'),
      modelWrap: q('[data-model-wrap]'),
      model: q('[data-model-filter]'),
      stock: q('[data-stock-filter]'),
      count: q('[data-result-count]'),
      browse: q('[data-browse-link]'),
      chips: q('[data-active-filters]'),
      grid: q('[data-catalog-grid]'),
      pagination: q('[data-pagination]')
    };
    var presets = Array.prototype.slice.call(root.querySelectorAll('[data-price]'));
    var pageSize = parseInt(root.getAttribute('data-page-size'), 10) || 12;
    var imgSizes = root.getAttribute('data-img-sizes') || '';
    var moneyFormat = root.getAttribute('data-money-format') || '${{amount}}';

    /* Collection labels / URLs come from the server-rendered <select> (filter_collection blocks). */
    var collectionsByHandle = {};
    if (els.collection) {
      Array.prototype.forEach.call(els.collection.options, function (option) {
        if (option.value && option.value !== 'all') {
          collectionsByHandle[option.value] = {
            label: option.textContent.trim(),
            url: option.getAttribute('data-url') || ''
          };
        }
      });
    }

    /* One pass: search haystack, parsed iPhone models, original (featured) position. */
    products.forEach(function (p, index) {
      p.collections = Array.isArray(p.collections) ? p.collections : [];
      p.category = p.category || '';
      var models = [];
      (Array.isArray(p.options) ? p.options : []).forEach(function (value) {
        var model = phoneModel(value);
        if (model && models.indexOf(model) === -1) models.push(model);
      });
      var hay = [p.title, p.category].concat(models);
      p.collections.forEach(function (handle) {
        if (collectionsByHandle[handle]) hay.push(collectionsByHandle[handle].label);
      });
      p._hay = hay.join(' ').toLowerCase();
      p._models = models;
      p._index = index;
    });

    var DEFAULT_STATE = { query: '', collection: 'all', page: 1, sort: 'featured', min: '', max: '', stock: false, model: '' };
    var state = Object.assign({}, DEFAULT_STATE);

    function money(cents) {
      return formatMoney(cents, moneyFormat);
    }

    function dollarsToMoney(value) {
      var n = Number(value);
      return isFinite(n) ? money(Math.round(n * 100)) : String(value);
    }

    function collectionLabel(handle) {
      return collectionsByHandle[handle] ? collectionsByHandle[handle].label : handle;
    }

    function priceLabel(p) {
      if (!(p.price > 0)) return esc(S.see_options);
      /* money() is plain text (escape it); S.from is the theme's "From {{ price }}" _html translation (raw, like Liquid). */
      var price = esc(money(p.price));
      return p.price_max > p.price ? fill(S.from, { price: price }) : price;
    }

    /* Same markup as snippets/atbt-product-card.liquid. */
    function card(p) {
      var title = esc(p.title);
      var url = esc(p.url);
      var image = p.image
        ? '<img src="' + esc(p.image) + '" srcset="' + esc(srcset(p.image)) + '" sizes="' + esc(imgSizes) +
          '" alt="' + esc(p.alt || p.title) + '" loading="lazy" width="600" height="600">'
        : '<span class="shop-card-noimage" aria-hidden="true"></span>';
      var count = Number(p.variants_count) || 0;
      return '<article class="shop-card"><div class="shop-card-picture"><a href="' + url + '" aria-label="' + title + '">' +
        image + '</a>' +
        (p.available ? '' : '<span class="stock-badge">' + esc(S.sold_out) + '</span>') +
        '<button type="button" class="shop-quick" data-quick="' + esc(p.handle) + '" aria-label="' + esc(S.quick_view) + ': ' + title + '">' +
        '<svg aria-hidden="true"><use href="#i-plus"/></svg><span>' + esc(S.quick_view) + '</span></button></div>' +
        '<div class="shop-card-body"><span class="shop-card-category">' + esc(p.category) + '</span>' +
        '<a href="' + url + '"><h3>' + title + '</h3></a>' +
        '<div class="shop-card-bottom"><span class="shop-card-price">' + priceLabel(p) + '</span>' +
        '<span class="shop-card-options">' + (count > 1 ? count + ' ' + esc(S.options) : esc(S.view_details)) + '</span>' +
        '</div></div></article>';
    }

    function emptyState(invalid) {
      return '<div class="catalog-empty"><h3>' + esc(S.empty_heading) + '</h3><p>' +
        esc(invalid ? S.invalid_text : S.empty_text) + '</p><button class="btn clear-filters" type="button">' +
        esc(S.clear) + '</button></div>';
    }

    function paginationMarkup(page, pages) {
      if (pages <= 1) return '';
      var html = '<button type="button" data-page="' + (page - 1) + '"' + (page === 1 ? ' disabled' : '') +
        ' aria-label="' + esc(S.prev) + '">←</button>';
      var visible = [1, pages, page - 1, page, page + 1]
        .filter(function (n, i, list) { return n > 0 && n <= pages && list.indexOf(n) === i; })
        .sort(function (a, b) { return a - b; });
      var previous = 0;
      visible.forEach(function (n) {
        if (previous && n - previous > 1) html += '<span class="page-gap">…</span>';
        html += '<button type="button" data-page="' + n + '"' + (n === page ? ' aria-current="page"' : '') +
          ' aria-label="' + esc(fill(S.page, { n: n })) + '">' + n + '</button>';
        previous = n;
      });
      html += '<button type="button" data-page="' + (page + 1) + '"' + (page === pages ? ' disabled' : '') +
        ' aria-label="' + esc(S.next) + '">→</button>';
      return html;
    }

    function baseProducts() {
      if (state.collection === 'all') return products;
      return products.filter(function (p) {
        return p.collections.indexOf(state.collection) !== -1;
      });
    }

    function toCents(value) {
      if (value === '' || value == null) return null;
      var n = Number(value);
      return isFinite(n) ? Math.round(n * 100) : null;
    }

    function matches() {
      var tokens = state.query.toLowerCase().split(/\s+/).filter(Boolean);
      var min = toCents(state.min);
      var max = toCents(state.max);
      var rows = baseProducts().filter(function (p) {
        if (state.stock && !p.available) return false;
        if (min !== null && p.price < min) return false;
        if (max !== null && p.price > max) return false;
        if (state.model && p._models.indexOf(state.model) === -1) return false;
        for (var i = 0; i < tokens.length; i++) {
          if (p._hay.indexOf(tokens[i]) === -1) return false;
        }
        return true;
      });
      /* "featured" keeps the collection order the JSON was rendered in. */
      if (state.sort === 'low') rows.sort(function (a, b) { return a.price - b.price || a._index - b._index; });
      else if (state.sort === 'high') rows.sort(function (a, b) { return b.price - a.price || a._index - b._index; });
      else if (state.sort === 'az') rows.sort(function (a, b) { return compareText(a.title, b.title) || a._index - b._index; });
      else if (state.sort === 'new') rows.sort(function (a, b) { return (b.created || 0) - (a.created || 0) || a._index - b._index; });
      return rows;
    }

    /* Rebuilds the iPhone model list for the current collection (design models()). */
    function models() {
      state.model = '';
      if (!els.model) return;
      var seen = {};
      var list = [];
      baseProducts().forEach(function (p) {
        p._models.forEach(function (m) {
          if (!seen[m]) {
            seen[m] = true;
            list.push(m);
          }
        });
      });
      list.sort(compareText);
      if (els.modelWrap) els.modelWrap.hidden = !list.length;
      els.model.innerHTML = '<option value="">' + esc(S.all_models) + '</option>' + list.map(function (m) {
        return '<option value="' + esc(m) + '">' + esc(m) + '</option>';
      }).join('');
    }

    function priceChipLabel() {
      if (state.min !== '' && state.max !== '') return dollarsToMoney(state.min) + '–' + dollarsToMoney(state.max);
      if (state.min !== '') return dollarsToMoney(state.min) + '+';
      return S.up_to + ' ' + dollarsToMoney(state.max);
    }

    function renderChips() {
      var chips = [];
      if (state.query) chips.push(['query', S.search_chip + ' ' + state.query]);
      if (state.collection !== 'all') chips.push(['collection', collectionLabel(state.collection)]);
      if (state.min !== '' || state.max !== '') chips.push(['price', priceChipLabel()]);
      if (state.model) chips.push(['model', state.model]);
      if (state.stock) chips.push(['stock', S.stock_chip]);
      if (els.chips) {
        els.chips.hidden = !chips.length;
        els.chips.innerHTML = chips.map(function (chip) {
          return '<button type="button" data-remove-filter="' + chip[0] + '" aria-label="' + esc(S.remove_filter + ': ' + chip[1]) + '">' +
            esc(chip[1]) + '<span aria-hidden="true">×</span></button>';
        }).join('');
      }
      var current = state.min + ',' + state.max;
      presets.forEach(function (button) {
        button.setAttribute('aria-pressed', String(button.getAttribute('data-price') === current));
      });
    }

    function renderBrowseLink() {
      if (!els.browse) return;
      var selected = state.collection !== 'all' ? collectionsByHandle[state.collection] : null;
      var href = selected && selected.url ? selected.url : S.browse_url;
      var label = selected ? S.explore + ' ' + selected.label : S.browse_label;
      els.browse.setAttribute('href', href);
      els.browse.innerHTML = esc(label) + ' <span aria-hidden="true">↗</span>';
    }

    function render() {
      renderChips();
      var invalid = state.min !== '' && state.max !== '' && Number(state.min) > Number(state.max);
      var rows = invalid ? [] : matches();
      var pages = Math.max(1, Math.ceil(rows.length / pageSize));
      state.page = Math.min(Math.max(1, state.page), pages);
      var start = (state.page - 1) * pageSize;

      if (els.count) {
        els.count.textContent = rows.length
          ? fill(S.results, { start: start + 1, end: Math.min(start + pageSize, rows.length), total: rows.length })
          : S.results_none;
      }
      if (els.grid) {
        els.grid.innerHTML = rows.length ? rows.slice(start, start + pageSize).map(card).join('') : emptyState(invalid);
      }
      if (els.pagination) {
        els.pagination.innerHTML = paginationMarkup(state.page, pages);
        els.pagination.hidden = pages <= 1;
      }
      renderBrowseLink();
    }

    function update() {
      state.page = 1;
      render();
    }

    function reset() {
      state = Object.assign({}, DEFAULT_STATE);
      if (els.search) els.search.value = '';
      if (els.sort) els.sort.value = 'featured';
      if (els.min) els.min.value = '';
      if (els.max) els.max.value = '';
      if (els.stock) els.stock.checked = false;
      if (els.collection) els.collection.value = 'all';
      models();
      render();
    }

    function removeFilter(key) {
      if (key === 'price') {
        state.min = '';
        state.max = '';
        if (els.min) els.min.value = '';
        if (els.max) els.max.value = '';
      } else if (key === 'stock') {
        state.stock = false;
        if (els.stock) els.stock.checked = false;
      } else if (key === 'collection') {
        state.collection = 'all';
        if (els.collection) els.collection.value = 'all';
        models();
      } else if (key === 'query') {
        state.query = '';
        if (els.search) els.search.value = '';
      } else if (key === 'model') {
        state.model = '';
        if (els.model) els.model.value = '';
      }
      update();
    }

    function goToPage(button) {
      var target = Number(button.getAttribute('data-page'));
      if (!isFinite(target)) return;
      var all = els.pagination ? els.pagination.querySelectorAll('button') : [];
      var kind = button === all[0] ? 'prev' : button === all[all.length - 1] ? 'next' : 'page';
      state.page = target;
      render();
      if (els.toolbar && typeof els.toolbar.scrollIntoView === 'function') {
        els.toolbar.scrollIntoView({ behavior: reducedMotion && reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
      }
      /* The clicked button was replaced: keep keyboard focus in the pagination. */
      if (els.pagination) {
        var buttons = els.pagination.querySelectorAll('button');
        var next = kind === 'prev' ? buttons[0] : kind === 'next' ? buttons[buttons.length - 1] : null;
        if (!next || next.disabled) next = els.pagination.querySelector('[aria-current="page"]');
        if (next) next.focus({ preventScroll: true });
      }
    }

    root.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;

      var preset = target.closest('[data-price]');
      if (preset) {
        var value = preset.getAttribute('data-price') || ',';
        if (value === state.min + ',' + state.max) {
          state.min = '';
          state.max = '';
        } else {
          var parts = value.split(',');
          state.min = parts[0] || '';
          state.max = parts[1] || '';
        }
        if (els.min) els.min.value = state.min;
        if (els.max) els.max.value = state.max;
        update();
        return;
      }

      var chip = target.closest('[data-remove-filter]');
      if (chip) {
        var focusAfter = chip.nextElementSibling || chip.previousElementSibling;
        var focusKey = focusAfter ? focusAfter.getAttribute('data-remove-filter') : null;
        removeFilter(chip.getAttribute('data-remove-filter'));
        var again = focusKey && els.chips ? els.chips.querySelector('[data-remove-filter="' + focusKey + '"]') : null;
        if (again) again.focus({ preventScroll: true });
        else if (els.search) els.search.focus({ preventScroll: true });
        return;
      }

      var pageButton = target.closest('[data-page]');
      if (pageButton) {
        if (!pageButton.disabled) goToPage(pageButton);
        return;
      }

      var clear = target.closest('.clear-filters');
      if (clear) {
        var fromGrid = els.grid && els.grid.contains(clear);
        reset();
        if (fromGrid && els.search) els.search.focus({ preventScroll: true });
        return;
      }

      if (target.closest('[data-filter-toggle]') && els.filters && els.toggle) {
        var open = els.filters.classList.toggle('open');
        els.toggle.setAttribute('aria-expanded', String(open));
      }
    });

    if (els.search) {
      var applySearch = function () {
        var next = els.search.value.trim();
        if (next === state.query) return;
        state.query = next;
        update();
      };
      els.search.addEventListener('input', debounce(applySearch, SEARCH_DELAY));
      els.search.addEventListener('keydown', function (event) {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        applySearch();
      });
    }

    var onPrice = debounce(function () {
      var min = els.min ? els.min.value : '';
      var max = els.max ? els.max.value : '';
      if (min === state.min && max === state.max) return;
      state.min = min;
      state.max = max;
      update();
    }, SEARCH_DELAY);
    if (els.min) els.min.addEventListener('input', onPrice);
    if (els.max) els.max.addEventListener('input', onPrice);

    if (els.sort) {
      els.sort.addEventListener('change', function () {
        state.sort = els.sort.value || 'featured';
        update();
      });
    }
    if (els.collection) {
      els.collection.addEventListener('change', function () {
        state.collection = els.collection.value || 'all';
        models();
        update();
      });
    }
    if (els.model) {
      els.model.addEventListener('change', function () {
        state.model = els.model.value;
        update();
      });
    }
    if (els.stock) {
      els.stock.addEventListener('change', function () {
        state.stock = els.stock.checked;
        update();
      });
    }

    /* Pick up values the browser restored on back/forward navigation. */
    if (els.search) state.query = els.search.value.trim();
    if (els.sort) state.sort = els.sort.value || 'featured';
    if (els.collection) state.collection = els.collection.value || 'all';
    if (els.min) state.min = els.min.value;
    if (els.max) state.max = els.max.value;
    if (els.stock) state.stock = els.stock.checked;

    models();
    var changed = state.query !== '' || state.sort !== 'featured' || state.collection !== 'all' ||
      state.min !== '' || state.max !== '' || state.stock;
    /* The first page is server-rendered with identical markup: only re-render when restored filters differ. */
    if (changed) render();
    else renderChips();
  }

  function initAll(scope) {
    var container = scope && typeof scope.querySelectorAll === 'function' ? scope : document;
    Array.prototype.forEach.call(container.querySelectorAll(ROOT_SELECTOR), init);
  }

  /* The script tag sits in the section, so it can run more than once (several instances, theme editor). */
  if (window.ATBTCatalog && typeof window.ATBTCatalog.initAll === 'function') {
    window.ATBTCatalog.initAll(document);
    return;
  }
  window.ATBTCatalog = { initAll: initAll };

  initAll(document);
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
